import type { PartBundle, PartRastreoBundle } from './types';
import { t } from '../lib/i18n';

export interface RastreoQuery {
  from: string;
  to: string;
  sid?: number | null;
}

export async function fetchPartRastreo(line: string, part: string, q: RastreoQuery): Promise<PartRastreoBundle> {
  const qs = new URLSearchParams({ from: q.from, to: q.to });
  if (q.sid != null) qs.set('sid', String(q.sid));
  const res = await fetch(
    `/api/part/${encodeURIComponent(line)}/${encodeURIComponent(part)}/rastreo?${qs.toString()}`,
    { credentials: 'same-origin' },
  );
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (res.status === 502 || res.status === 503) throw new Error(t.errors.daemonOffline);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  // Go side writes the daemon's `data` payload directly (same pattern as
  // /api/purchases, /api/quotes, etc.) — no {ok, data} envelope to unwrap.
  return (await res.json()) as PartRastreoBundle;
}

const DAEMON_TIMEOUT_MS = 30_000;

export class PartNotFoundError extends Error {
  constructor() { super('part not found'); this.name = 'PartNotFoundError'; }
}

export async function fetchPart(line: string, part: string): Promise<PartBundle> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), DAEMON_TIMEOUT_MS);
  try {
    const q = new URLSearchParams({ line, part });
    const res = await fetch(`/api/part?${q}`, { credentials: 'same-origin', signal: ctl.signal });
    if (res.status === 404) throw new PartNotFoundError();
    if (res.status === 502 || res.status === 503) throw new Error(t.errors.daemonOffline);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // Go's /api/part handler writes the daemon's inner `data` payload
    // directly (resp.Data, not the {ok,data} envelope). Tests passed an
    // envelope-shaped stub which hid the mismatch in CI.
    return (await res.json()) as PartBundle;
  } finally {
    clearTimeout(timer);
  }
}
